/* DCG v2-9 · 10 OCT 2026 · D-SP-24 · grid / list switch for StorePass results; one switch node for the life of the page (v2-9) */
/* =========================================================
   Desert City Games — grid / list view for StorePass results (D-SP-24)
   File: assets/dcg-storepass-view.js

   On StorePass collection and search pages this adds a two-button switch
   to StorePass's results bar, beside Sort, and remembers the visitor's
   choice on the device.

   WHAT IT DOES, AND ALL IT DOES
   - sets data-dcg-view="grid" | "list" on #product-listing-container;
   - keeps one switch (.dcg-view-switch) as the last child of StorePass's
     results bar (.store-pass-results-header), putting it back when
     StorePass redraws the bar;
   - stores the choice in localStorage under "dcg-sp-view".

   WHAT IT DOES NOT DO
   The list is not another card. It is the same StorePass card, with the
   same controls and the same things the DCG guard adds to it, laid out in
   a row by assets/dcg-storepass.css under [data-dcg-view="list"]. This
   file touches no card, makes no request, reads no response and never
   calls into storepass.js or the guard. So the guard states, the
   zero-price rule, the printing stock rule and the cart enforcer cannot
   behave differently in the two views: they do not know which one is on.

   The attribute is on the results container, which StorePass renders
   into and never rewrites, so the view holds through Sort, paging and a
   filter change without this file doing anything.

   StorePass's own "vertical-list" item style is not used: it is a
   numbered ranking (number, picture, name, price) with no condition
   selector, no quantity and no Add to Cart, and it is one setting for the
   whole store, not something a visitor can switch
   (docs/REPORT-v2-8-1.md, item 3a).

   Grid is the default. If localStorage cannot be read or written (private
   mode, a blocked cookie setting) the switch still works for the page.
   ========================================================= */
(function () {
  'use strict';

  window.DCG = window.DCG || {};
  if (window.DCG.storepassView) return;

  const KEY = 'dcg-sp-view';
  const ATTR = 'data-dcg-view';
  const VIEWS = ['grid', 'list'];
  const NAMES = { grid: 'Grid', list: 'List' };
  const SWITCH_CLASS = 'dcg-view-switch';

  const listing = () => document.getElementById('product-listing-container');

  function saved() {
    try {
      const v = window.localStorage.getItem(KEY);
      return VIEWS.indexOf(v) !== -1 ? v : 'grid';
    } catch (_) { return 'grid'; }
  }
  function save(v) {
    try { window.localStorage.setItem(KEY, v); } catch (_) { /* private mode, quota */ }
  }

  let view = saved();

  // The switch: two buttons in a group. The one that is on is pressed.
  function buildSwitch() {
    const group = document.createElement('div');
    group.className = SWITCH_CLASS;
    group.setAttribute('role', 'group');
    group.setAttribute('aria-label', 'View');
    VIEWS.forEach(v => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'dcg-view-btn';
      button.setAttribute('data-dcg-view-set', v);
      button.setAttribute('aria-label', NAMES[v] + ' view');
      button.title = NAMES[v] + ' view';
      const text = document.createElement('span');
      text.className = 'dcg-view-btn__text';
      text.textContent = NAMES[v];
      button.appendChild(text);
      button.addEventListener('click', e => {
        e.preventDefault();
        set(v, true);
      });
      group.appendChild(button);
    });
    return group;
  }

  // Which button is pressed, on every switch that is in the page.
  function mark() {
    const root = listing();
    if (!root) return;
    const buttons = root.querySelectorAll('.' + SWITCH_CLASS + ' .dcg-view-btn');
    for (let i = 0; i < buttons.length; i++) {
      const on = buttons[i].getAttribute('data-dcg-view-set') === view ? 'true' : 'false';
      if (buttons[i].getAttribute('aria-pressed') !== on) buttons[i].setAttribute('aria-pressed', on);
    }
  }

  function apply() {
    const root = listing();
    if (!root) return;
    if (root.getAttribute(ATTR) !== view) root.setAttribute(ATTR, view);
    mark();
  }

  function set(v, remember) {
    if (VIEWS.indexOf(v) === -1) return;
    const changed = v !== view;
    view = v;
    if (remember) save(v);
    apply();
    if (changed) {
      try { document.dispatchEvent(new CustomEvent('dcg:storepass-view', { detail: { view: v } })); } catch (_) {}
    }
  }

  // One switch, the last child of StorePass's results bar. Appended, never
  // put between StorePass's own nodes; CSS (order) puts it before Sort.
  //
  // DCG v2-9: it is ONE node for the life of the page. Before, a bar that
  // StorePass replaced got a newly built switch. StorePass redraws its bar
  // while results and pictures are still loading, and a click that began on
  // the old switch and ended after it was replaced was lost (Cowork, 10 OCT:
  // a first click on List that did nothing during loading; not reproduced,
  // made impossible). The same node is moved into the new bar, and it is
  // not touched at all while it is already the bar's last child.
  let theSwitch = null;
  function place() {
    const root = listing();
    if (!root) return;
    const bar = root.querySelector('.store-pass-results-header');
    if (!bar) return;
    if (!theSwitch) theSwitch = buildSwitch();
    const all = root.querySelectorAll('.' + SWITCH_CLASS);
    for (let i = 0; i < all.length; i++) {
      if (all[i] !== theSwitch && all[i].parentNode) all[i].parentNode.removeChild(all[i]);   // never two
    }
    if (theSwitch.parentNode !== bar || bar.lastElementChild !== theSwitch) bar.appendChild(theSwitch);
    mark();
  }

  let queued = false;
  function soon() {
    if (queued) return;
    queued = true;
    setTimeout(() => {
      queued = false;
      try { apply(); place(); } catch (_) { /* never into StorePass */ }
    }, 0);
  }

  function start() {
    const root = listing();
    if (!root) return;            // not a StorePass results page
    apply();
    place();
    if (typeof MutationObserver !== 'undefined') {
      new MutationObserver(soon).observe(root, { childList: true, subtree: true });
    }
    // the choice made in another tab of this browser
    window.addEventListener('storage', e => {
      if (e && e.key === KEY) set(saved(), false);
    });
  }

  window.DCG.storepassView = {
    get: () => view,
    set: v => set(v, true),
    _key: KEY
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
